// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";

import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { importTippyCssFiles } from "@pace/App/Components/Tippy/tippy.helper";
import { getSystemColor, getSystemZIndex, xClassNames } from "@pace/XDL/Utilities";
import React, { useEffect, useRef, useState } from "react";
import styled, { css, withTheme } from "styled-components";

export interface TooltipPresentationProps extends TippyProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        tooltipClickable?: StyledProp<TooltipProps>;
        tooltipBody?: StyledProp<TooltipProps>;
        tooltipContainer?: StyledProp<TooltipProps>;
        tooltipWrapper?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
    // Xng-Change
    onClick?: () => void;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement;
        /** The text that will appear in the tooltip */
        text?: string | React.ReactElement | React.ReactNode | React.ReactNode[];
        /** Function called on tooltip open */
        onOpen?: React.EventHandler<React.SyntheticEvent>;
        /** Function called on tooltip close */
        onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;
    }
>;

type TooltipProps = TooltipComponentProps & TooltipPresentationProps & TippyProps;

const tooltipArrow = css`
    .tippy-arrow {
        &,
        &::before {
            position: absolute;
            width: 24px;
            height: 24px;
            z-index: 1;
        }

        &::before {
            content: "";
            background: ${getSystemColor("common.white")};
            border-color: ${getSystemColor("common.ui12")};
            border-style: solid;
            border-width: 0 1px 1px 0;
            border-radius: 0 0 6px 0;
        }
    }

    &[data-placement^="top"] > .tippy-arrow {
        bottom: -12px;
        &:before {
            transform: scale(0.8, 1) rotate(45deg);
        }
    }

    &[data-placement^="bottom"] > .tippy-arrow {
        top: -12px;
        &:before {
            transform: scale(0.8, 1) rotate(-135deg);
        }
    }

    &[data-placement^="left"] > .tippy-arrow {
        right: -12px;
        &:before {
            transform: scale(0.8, 1) rotate(-45deg);
        }
    }

    &[data-placement^="right"] > .tippy-arrow {
        left: -12px;
        &:before {
            transform: scale(0.8, 1) rotate(135deg);
        }
    }
`;

const TooltipContainer = styled.div<InjectableCss<any>>`
    ${tooltipArrow}
    &.CMS-Mode {
        background-color: ${getSystemColor("common.ui6")};
        max-width: 198px;
        border-radius: 6px;
        min-width: 140px;
    }
    ${injectCss}
`;

const TooltipBody = styled.div<InjectableCss<any>>`
    position: relative;
    z-index: 2;
    color: ${getSystemColor("text.main")};
    &.CMS-Mode {
        display: flex;
        flex-direction: column;
        flex: 0 0 auto;
        padding: 8px;
        & ${TypographyStyle} {
            color: ${getSystemColor("common.white")};
            font-size: 14px;
            font-weight: normal;
            line-height: 22px;
            text-align: center;
            letter-spacing: 0;
            &:hover {
                text-decoration: none;
            }
        }
    }

    ${injectCss}
`;

const TooltipWrapper = styled.div<InjectableCss<any>>`
    ${injectCss}
`;

// necessary to use theme clickable styles and instance clickable overrides
// Xng-Change PAC-1315: make more-info span unselectable so it will not show in the copy paste
const TooltipClickable = styled.span.attrs({ tabIndex: 0 })<InjectableCss<any>>`
    display: inline-block;
    vertical-align: middle;
    & > ${StyledClickable} {
        display: inline-block;
    }
    &:focus {
        outline: none;
    }

    &&& ${IconWrapper} {
        margin-right: 0;
    }
    
    & .tooltip-more-info {
        -webkit-user-select: none; /* Safari */
        -moz-user-select: none; /* Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
        user-select: none; /* Standard */
    }
    ${injectCss}
`;

/**
 * Component that provides supplementatal information on click or enter.
 */

const Tooltip: React.FC<TooltipProps> = props => {
    // XNG-Changes: To fix tooltip style in CMS field tooltip
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [cmsClassNames, setCmsClassNames] = useState("");

    /**
     * XNG-Changes: To handle tooltip style in left-nav sidebar on CMS
     */
    const isCMSMode = () => {
        const isCmsPage = tooltipRef.current?.closest("[data-scroll-container]");
        if (!isCmsPage) {
            setCmsClassNames("");
            return;
        }
        setCmsClassNames("CMS-Mode");
    };

    useEffect(() => {
        importTippyCssFiles();
        isCMSMode();
    }, []);

    const { mergeCss, text, triggerComponent, triggerAltText, iconProps, onOpen, onClose, onClick, ...otherProps } = props;

    // Xng-Change
    const handleTooltipClick = () => {
        onClick?.();
    };

    if (
        !text ||
        (typeof text === "string" && text.length === 0) ||
        (Array.isArray(text) && text.length === 0)
    ) {
        return triggerComponent || null;
    }

    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.tooltip?.defaultProps?.mergeCss;
    const { spreadProps } = applyPropBuilder(props, { component: "tooltip" });
    const { applyStyledProp } = applyPropBuilder(props, { component: "tooltip" });

    let trigger: {} | null | undefined;

    if (triggerComponent) {
        trigger = triggerComponent;
    } else {
        trigger = <IconMemo {...spreadProps("iconProps")} />;
    }

    const toolTipComponent = (
        <Typography as="p" {...spreadProps("typographyProps")}>
            {text}
        </Typography>
    );

    return (
        <TooltipWrapper
            ref={tooltipRef}
            className="hide-on-print tooltip-container"
            css={applyStyledProp(["cssOverrides", "tooltipWrapper"], resolvedMergeCss)}
        >
            <Tippy
                onShow={instance => {
                    onOpen?.({ target: instance.reference, instance } as any);
                }}
                onHide={instance => {
                    onClose?.({ target: instance.reference, instance } as any);
                }}
                render={attrs => (
                    <TooltipContainer
                        css={applyStyledProp(["cssOverrides", "tooltipContainer"], resolvedMergeCss)}
                        {...attrs}
                        className={xClassNames.append(cmsClassNames)}
                    >
                        <TooltipBody
                            css={applyStyledProp(["cssOverrides", "tooltipBody"], resolvedMergeCss)}
                            className={xClassNames.append(cmsClassNames)}
                        >
                            {toolTipComponent}
                        </TooltipBody>
                        <span data-popper-arrow="" className="tippy-arrow" />
                    </TooltipContainer>
                )}
                offset={({ placement }) => {
                    if (placement === "bottom-start" || placement === "top-start") {
                        return [-20, 20];
                    }

                    return [20, 20];
                }}
                zIndex={getSystemZIndex("tooltip")}
                appendTo={() => document.body}
                {...omitMultiple(otherProps, ["cssOverrides", "typographyProps", "theme"])}
            >
                {/* xng-change */}
                {/* add onClick to the TooltipClickable */}
                <TooltipClickable
                    css={applyStyledProp(["cssOverrides", "tooltipClickable"], resolvedMergeCss)}
                    data-test-selector="tooltip_OpenTooltip"
                    role="tooltip"
                    onClick={handleTooltipClick}
                >
                    {trigger}
                    <VisuallyHidden className="tooltip-more-info">{triggerAltText || props.theme!.translate("more info")}</VisuallyHidden>
                </TooltipClickable>
            </Tippy>
        </TooltipWrapper>
    );
};

Tooltip.defaultProps = {
    trigger: "mouseenter focus",
    placement: "bottom",
    interactive: true,
    animation: false,
};

/** @component */
export default withTheme(Tooltip);
